import * as XLSX from 'xlsx'
import type { PersonalizationRow } from '@/types'
import { MAX_PERSONALIZATION_ROWS } from '@/lib/personalization'

/**
 * Name & number from Excel (Duncan, 2026-10-09): the team downloads a
 * SIZE / NAME / NO template, fills one row per piece (or pastes the customer's
 * list) and uploads it. The file decides the size quantities as well as the
 * names, so one upload fills the whole item. Parsed in the browser only.
 *
 * Limits mirror StoreOrderRequest: 200 rows, name 100 chars, number 10 chars.
 */

const NAME_MAX = 100
const NUMBER_MAX = 10

export interface NameImport {
  sizeMatrix: Record<string, number>
  rows: PersonalizationRow[]
  /** Excel row numbers kept with a blank name or number. */
  incomplete: number[]
}

/** Download the template, prefilled with the rows already typed (if any). */
export function downloadNameTemplate(productName: string, sizes: string[], rows: PersonalizationRow[]): void {
  const data: string[][] = [['SIZE', 'NAME', 'NO']]
  for (const row of rows) data.push([row.size, row.name, row.number])

  const sheet = XLSX.utils.aoa_to_sheet(data)
  // NO is text so Excel keeps the zero in 07; blank rows below get the same format.
  for (let r = 1; r <= MAX_PERSONALIZATION_ROWS; r++) {
    const ref = XLSX.utils.encode_cell({ r, c: 2 })
    const cell = sheet[ref] ?? { t: 's', v: '' }
    sheet[ref] = { ...cell, t: 's', v: String(cell.v ?? ''), z: '@' }
  }
  sheet['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: MAX_PERSONALIZATION_ROWS, c: 2 } })
  sheet['!cols'] = [{ wch: 10 }, { wch: 28 }, { wch: 8 }]

  const help = XLSX.utils.aoa_to_sheet([
    ['HOW TO FILL'],
    ['One row per piece: SIZE, NAME and NO.'],
    ['The size quantities are counted from this list when you upload it.'],
    [`Sizes for this product: ${sizes.join(', ')}`],
    [`Up to ${MAX_PERSONALIZATION_ROWS} pieces. Name up to ${NAME_MAX} characters, number up to ${NUMBER_MAX}.`],
    ["If Excel drops the zero in 07, type it as '07."],
  ])
  help['!cols'] = [{ wch: 70 }]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'NAMES')
  XLSX.utils.book_append_sheet(workbook, help, 'READ ME')
  const slug = productName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'product'
  XLSX.writeFile(workbook, `${slug}-names.xlsx`)
}

const key = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '')

const SIZE_HEADERS = ['SIZE', 'SAIZ']
const NAME_HEADERS = ['NAME', 'NAMA']
const NUMBER_HEADERS = ['NO', 'NUMBER', 'NOMBOR', 'NUM']
const SIZE_ALIASES: Record<string, string> = { XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', '2XL': 'XXL', '3XL': 'XXXL', '4XL': 'XXXXL' }

/** First few row numbers, for an error message. */
const listRows = (rows: number[]) => rows.slice(0, 5).join(', ') + (rows.length > 5 ? ` and ${rows.length - 5} more` : '')

/**
 * Read an uploaded list. Refuses the whole file (returns `error`) on a missing
 * header, an unknown size, a value over the server limit or too many rows, so
 * the form is never half-replaced. Rows come back in product size order, file
 * order kept within a size - the order syncPersonalizations keeps.
 */
export async function parseNameFile(file: File, sizes: string[]): Promise<NameImport | { error: string }> {
  let table: string[][]
  try {
    // raw: true keeps CSV text as typed, so 07 stays 07.
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', raw: true })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    table = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' })
  } catch {
    return { error: 'This file could not be read. Upload the Excel template (.xlsx).' }
  }

  const headerIndex = table.findIndex((row) => {
    const keys = row.map((cell) => key(String(cell)))
    return keys.some((k) => SIZE_HEADERS.includes(k)) && keys.some((k) => NAME_HEADERS.includes(k)) && keys.some((k) => NUMBER_HEADERS.includes(k))
  })
  if (headerIndex < 0) return { error: 'SIZE, NAME and NO columns were not found. Use the downloaded template.' }

  const header = table[headerIndex].map((cell) => key(String(cell)))
  const sizeCol = header.findIndex((k) => SIZE_HEADERS.includes(k))
  const nameCol = header.findIndex((k) => NAME_HEADERS.includes(k))
  const numberCol = header.findIndex((k) => NUMBER_HEADERS.includes(k))

  const sizeByKey = new Map(sizes.map((size) => [key(size), size]))
  const bySize = new Map<string, PersonalizationRow[]>()
  const unknown: number[] = []
  const tooLong: number[] = []
  const incomplete: number[] = []
  let count = 0

  table.slice(headerIndex + 1).forEach((row, i) => {
    const excelRow = headerIndex + i + 2
    const sizeText = String(row[sizeCol] ?? '').trim()
    const name = String(row[nameCol] ?? '').trim()
    const number = String(row[numberCol] ?? '').trim()
    if (!sizeText && !name && !number) return

    const sizeKey = key(sizeText)
    const size = sizeByKey.get(sizeKey) ?? sizeByKey.get(SIZE_ALIASES[sizeKey] ?? '')
    if (!size) {
      unknown.push(excelRow)
      return
    }
    if (name.length > NAME_MAX || number.length > NUMBER_MAX) tooLong.push(excelRow)
    if (!name || !number) incomplete.push(excelRow)
    count++
    bySize.set(size, [...(bySize.get(size) ?? []), { size, name, number }])
  })

  if (unknown.length > 0) {
    return { error: `Row ${listRows(unknown)}: size not offered. Use one of ${sizes.join(', ')}.` }
  }
  if (tooLong.length > 0) {
    return { error: `Row ${listRows(tooLong)}: name over ${NAME_MAX} or number over ${NUMBER_MAX} characters.` }
  }
  if (count === 0) return { error: 'The list is empty - add one row per piece under SIZE, NAME and NO.' }
  if (count > MAX_PERSONALIZATION_ROWS) {
    return { error: `${count} pieces in the file - one item takes up to ${MAX_PERSONALIZATION_ROWS}. Split the list into two orders.` }
  }

  const sizeMatrix: Record<string, number> = {}
  const rows: PersonalizationRow[] = []
  for (const size of sizes) {
    const sizeRows = bySize.get(size)
    if (!sizeRows) continue
    sizeMatrix[size] = sizeRows.length
    rows.push(...sizeRows)
  }
  return { sizeMatrix, rows, incomplete }
}
